'use client'

import * as React from 'react'
import { useLocale, useTranslations, useFormatter } from 'next-intl'
import { typographyVariants } from '@repo/design-system-cf/components/typography'
import { ChevronDownIcon } from 'cf-menu/components/icons/chevron-down-icon'
import { IconChevronLeft } from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'
import { Link } from '@repo/web-ui/components/link'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { generatePageGroups } from '@web/utils/products-pagination'
import { NEXT_PUBLIC_CF_URL } from '@web/utils/env'

export function ProductsWithOrderByAndPaginationControls({
  selectedPage,
  total,
  perPage,
  orderBy,
  baseURL,
  scroll = false,
  hideSortControls = false,
  includeOrderByInUrl = true,
  includeOrderByInFirstPageUrl = true,
  useLatestSortLabel = false,
  newestSortValue = 'date',
}: {
  selectedPage: number
  total: number
  perPage: number
  orderBy: string
  baseURL: string
  scroll?: boolean
  hideSortControls?: boolean
  includeOrderByInUrl?: boolean
  includeOrderByInFirstPageUrl?: boolean
  useLatestSortLabel?: boolean
  // Legacy WP pages differ on the "newest" orderby value (e.g. freebies use `new`).
  newestSortValue?: string
}) {
  const t = useTranslations('components.products_pagination')
  const format = useFormatter()
  return (
    <div className="flex flex-col gap-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-5">
        {!hideSortControls ? (
          <div className="flex gap-2">
            <SortControls
              orderBy={orderBy}
              useLatestSortLabel={useLatestSortLabel}
              newestSortValue={newestSortValue}
            />
          </div>
        ) : null}
        <div className={tw(typographyVariants({ variant: 'body-compact-01' }), 'text-cf-grey-700')}>
          {t('showing_x_of_y', {
            totalCount: total,
            from: format.number((selectedPage - 1) * perPage + 1),
            to: format.number(Math.min(selectedPage * perPage, total)),
            total: format.number(total),
          })}
        </div>
      </div>
      <Pagination
        selectedPage={selectedPage}
        total={total}
        perPage={perPage}
        orderBy={orderBy}
        baseURL={baseURL}
        scroll={scroll}
        includeOrderByInUrl={includeOrderByInUrl}
        includeOrderByInFirstPageUrl={includeOrderByInFirstPageUrl}
      />
    </div>
  )
}

export function SortControls({
  orderBy,
  useLatestSortLabel = false,
  newestSortValue = 'date',
}: {
  orderBy: string
  useLatestSortLabel?: boolean
  newestSortValue?: string
}) {
  const t = useTranslations('components.products_pagination')

  const handleOrderByChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === orderBy) {
      return
    }
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('orderby', e.target.value)
    const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`
    window.location.href = newRelativePathQuery
  }

  return (
    <div className="relative">
      <select
        name="orderby"
        defaultValue={orderBy}
        onChange={handleOrderByChange}
        className={tw(
          'border-cf-grey-400 hover:bg-cf-grey-50 appearance-none rounded border bg-transparent px-2 py-1.5 pr-7',
          typographyVariants({ variant: 'body-compact-01' }),
        )}
        aria-label={t('order_by', {
          type: orderBy === 'popularity' ? 'popularity' : 'newest',
        })}
      >
        <option value="popularity">{t('order_by', { type: t('popularity') })}</option>
        <option value={newestSortValue}>
          {t('order_by', { type: t(useLatestSortLabel ? 'latest' : 'newest') })}
        </option>
      </select>
      <ChevronDownIcon className="text-cf-grey-600 pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" />
    </div>
  )
}

export function Pagination({
  selectedPage,
  total,
  perPage,
  orderBy,
  baseURL,
  scroll = false,
  includeOrderByInUrl = true,
  includeOrderByInFirstPageUrl = true,
}: {
  selectedPage: number
  total: number
  perPage: number
  orderBy: string
  baseURL: string
  scroll?: boolean
  includeOrderByInUrl?: boolean
  // Freebies page 1 is the landing page, where the sort order is meaningless.
  includeOrderByInFirstPageUrl?: boolean
}) {
  const locale = useLocale()
  const {
    shouldShowBackButton,
    firstPages,
    shouldShowMiddleEllipsis,
    middleEllipsisPage,
    lastPages,
    shouldShowEndEllipsis,
    endEllipsisPage,
    shouldShowNextButton,
  } = generatePageGroups({
    total,
    perPage,
    selectedPage,
  })
  const orderByParam = includeOrderByInUrl ? `?orderby=${orderBy}` : ''
  const totalPages = Math.ceil(total / perPage)
  const firstPageUrl = includeOrderByInFirstPageUrl ? `${baseURL}${orderByParam}` : `${baseURL}/`
  const prevPageUrl =
    selectedPage === 2 ? firstPageUrl : `${baseURL}/page/${selectedPage - 1}${orderByParam}`
  const nextPageUrl = `${baseURL}/page/${selectedPage + 1}${orderByParam}`
  const middleEllipsisPageUrl = `${baseURL}/page/${middleEllipsisPage}${orderByParam}`
  const endEllipsisPageUrl = `${baseURL}/page/${endEllipsisPage}${orderByParam}`

  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      {selectedPage <= totalPages ? (
        <>
          {shouldShowBackButton ? (
            <>
              <link
                rel="prev"
                href={`${NEXT_PUBLIC_CF_URL}${addOrReplaceLocaleInUrl(prevPageUrl, locale)}`}
              />
              <PaginationButton
                className="h-8 w-8 p-0"
                pageNumber={selectedPage - 1}
                selectedPage={selectedPage}
                url={prevPageUrl}
                scroll={scroll}
              >
                <IconChevronLeft className="h-5 w-5 shrink-0 stroke-2" />
              </PaginationButton>
            </>
          ) : null}
          {firstPages.map(pageNumber => (
            <PaginationButton
              key={pageNumber}
              pageNumber={pageNumber}
              selectedPage={selectedPage}
              url={pageNumber === 1 ? firstPageUrl : `${baseURL}/page/${pageNumber}${orderByParam}`}
              scroll={scroll}
            />
          ))}
          {shouldShowMiddleEllipsis ? (
            <PaginationButton
              className="h-8 w-8 p-0"
              pageNumber={middleEllipsisPage || 1}
              selectedPage={selectedPage}
              url={middleEllipsisPageUrl}
              scroll={scroll}
            >
              ...
            </PaginationButton>
          ) : null}
          {shouldShowMiddleEllipsis && Number(firstPages?.length) < 8
            ? lastPages.map(pageNumber => (
                <PaginationButton
                  key={pageNumber}
                  pageNumber={pageNumber}
                  selectedPage={selectedPage}
                  url={`${baseURL}/page/${pageNumber}${orderByParam}`}
                  scroll={scroll}
                />
              ))
            : null}
          {shouldShowEndEllipsis ? (
            <PaginationButton
              className="h-8 w-8 p-0"
              pageNumber={endEllipsisPage || totalPages}
              selectedPage={selectedPage}
              url={endEllipsisPageUrl}
              scroll={scroll}
            >
              ...
            </PaginationButton>
          ) : null}
          {shouldShowNextButton ? (
            <>
              <link
                rel="next"
                href={`${NEXT_PUBLIC_CF_URL}${addOrReplaceLocaleInUrl(nextPageUrl, locale)}`}
              />
              <PaginationButton
                className="h-8 w-8 p-0"
                pageNumber={totalPages}
                selectedPage={selectedPage}
                url={nextPageUrl}
                scroll={scroll}
              >
                <IconChevronLeft className="h-5 w-5 shrink-0 rotate-180 stroke-2" />
              </PaginationButton>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

function PaginationButton({
  pageNumber,
  selectedPage,
  url,
  scroll = false,
  className = '',
  children,
}: {
  pageNumber: number
  selectedPage: number
  url: string
  scroll?: boolean
  className?: string
  children?: React.ReactNode
}) {
  const t = useTranslations('components.products_pagination')
  return (
    <Link
      tabIndex={pageNumber === selectedPage ? -1 : 0}
      href={addOrReplaceLocaleInUrl(url)}
      className={tw(
        'flex min-h-8 min-w-8 shrink-0 items-center justify-center px-3 py-1.5',
        typographyVariants({ variant: 'body-compact-01' }),
        pageNumber === selectedPage
          ? 'bg-cf-sf-gray-1100 hover:bg-cf-sf-gray-1000 text-cf-white pointer-events-none font-bold'
          : 'text-cf-grey-600 bg-[#43454B06] font-medium hover:bg-[#43454B0D]',
        className,
      )}
      aria-current={pageNumber === selectedPage ? 'page' : undefined}
      aria-label={t('go_to_page', { page: pageNumber })}
      component="Link"
      scroll={scroll}
    >
      {children || pageNumber}
    </Link>
  )
}
